/**
 * The Unicode Character Database, as much of it as the inspector needs: every
 * character's name, general category, script, block and the version that added it,
 * plus name aliases and HTML named character references.
 *
 * Read from `public/data/unicode-names.txt`, built by `scripts/build-unicode-names.mjs`
 * from the UCD (Unicode License v3) and the WHATWG entity list (CC BY 4.0). The file is
 * fetched once, when the inspector first has something to inspect.
 *
 * ## The format
 *
 * Lines starting with `#` are comments (source, license). Each `@key value` line is one
 * table; `@names` is followed by the name lines, one per stored name:
 *
 * - `@gc`, `@scripts`, `@ages` — value lists, space-separated.
 * - `@gcRuns`, `@scriptRuns`, `@ageRuns` — runs of code points with equal value, as
 *   comma-separated `<gap>.<index>`: the gap from the previous run's first code point
 *   and the value's index in its list, both base 36.
 * - `@blocks`, `@rules` — `|`-separated `<first>.<last>.<text>` ranges (base 36): block
 *   names, and the prefixes of names derived from code points ("CJK UNIFIED
 *   IDEOGRAPH-" + hex).
 * - `@aliases` — `|`-separated `<code point>:<type>:<alias>`, type c (control),
 *   a (abbreviation), r (correction) or l (alternate).
 * - `@entities` — `|`-separated `<code point>:<name>,<name>…`, shortest name first.
 * - name lines — `[<gap>:]<shared><words>`: the gap from the previous named code point
 *   (omitted when 1), then one character giving how many leading words this name shares
 *   with the previous one, then the remaining words.
 *
 * Hangul syllables are named by the algorithm in the Unicode Standard (section 3.12)
 * rather than stored.
 */

export interface NameAliases {
  control?: string;
  abbreviation?: string;
  correction?: string;
  alternate?: string;
}

export interface UnicodeDatabase {
  version: string;
  /** The Name property, or null where Unicode assigns none (controls, unassigned…). */
  name: (codePoint: number) => string | null;
  aliases: (codePoint: number) => NameAliases;
  /** Two-letter General_Category, e.g. "Lu". */
  generalCategory: (codePoint: number) => string;
  /** Script property value, e.g. "Latin", "Old_Italic", "Common". */
  script: (codePoint: number) => string;
  /** Block name, e.g. "Basic Latin", or "No_Block". */
  block: (codePoint: number) => string;
  /** The Unicode version that added the character, e.g. "1.1", or "unassigned". */
  age: (codePoint: number) => string;
  /** HTML named character references without "&" and ";", shortest first. */
  entities: (codePoint: number) => readonly string[];
}

/** Where `scripts/build-unicode-names.mjs` writes the database. */
export const UNICODE_DATA_URL = "/data/unicode-names.txt";

const SHARED_DIGITS =
  "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LINE_BREAK = /\r?\n/;

/* Hangul syllable names: Unicode Standard section 3.12 and Jamo.txt. */
const S_BASE = 0xac_00;
const T_COUNT = 28;
const N_COUNT = 588;
/** L_COUNT × N_COUNT: 19 leading consonants × 588 vowel–trailing pairs. */
const S_COUNT = 19 * N_COUNT;
const JAMO_L = [
  "G",
  "GG",
  "N",
  "D",
  "DD",
  "R",
  "M",
  "B",
  "BB",
  "S",
  "SS",
  "",
  "J",
  "JJ",
  "C",
  "K",
  "T",
  "P",
  "H",
];
const JAMO_V = [
  "A",
  "AE",
  "YA",
  "YAE",
  "EO",
  "E",
  "YEO",
  "YE",
  "O",
  "WA",
  "WAE",
  "OE",
  "YO",
  "U",
  "WEO",
  "WE",
  "WI",
  "YU",
  "EU",
  "YI",
  "I",
];
const JAMO_T = [
  "",
  "G",
  "GG",
  "GS",
  "N",
  "NJ",
  "NH",
  "D",
  "L",
  "LG",
  "LM",
  "LB",
  "LS",
  "LT",
  "LP",
  "LH",
  "M",
  "B",
  "BS",
  "S",
  "SS",
  "NG",
  "J",
  "C",
  "K",
  "T",
  "P",
  "H",
];

/** "HANGUL SYLLABLE GA" for U+AC00, or null outside the syllable block. */
export function hangulSyllableName(codePoint: number): string | null {
  const index = codePoint - S_BASE;
  if (index < 0 || index >= S_COUNT) {
    return null;
  }
  const l = Math.floor(index / N_COUNT);
  const v = Math.floor((index % N_COUNT) / T_COUNT);
  const t = index % T_COUNT;
  return `HANGUL SYLLABLE ${JAMO_L[l]}${JAMO_V[v]}${JAMO_T[t]}`;
}

/** Sorted run starts and the value index of each run. */
interface Runs {
  starts: number[];
  values: number[];
}

function parseRuns(encoded: string): Runs {
  const starts: number[] = [];
  const values: number[] = [];
  let start = 0;
  for (const pair of encoded.split(",")) {
    const dot = pair.indexOf(".");
    start += Number.parseInt(pair.slice(0, dot), 36);
    starts.push(start);
    values.push(Number.parseInt(pair.slice(dot + 1), 36));
  }
  return { starts, values };
}

/** The index of the last element ≤ `value` in a sorted array, or -1. */
function floorIndex(sorted: readonly number[], value: number): number {
  let low = 0;
  let high = sorted.length - 1;
  let found = -1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    if (sorted[middle] <= value) {
      found = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return found;
}

function runLookup(runs: Runs, names: readonly string[], fallback: string) {
  return (codePoint: number): string => {
    const index = floorIndex(runs.starts, codePoint);
    return index === -1 ? fallback : (names[runs.values[index]] ?? fallback);
  };
}

interface Range {
  first: number;
  last: number;
  text: string;
}

function parseRanges(encoded: string): Range[] {
  if (!encoded) {
    return [];
  }
  return encoded
    .split("|")
    .map((entry) => {
      const firstDot = entry.indexOf(".");
      const secondDot = entry.indexOf(".", firstDot + 1);
      return {
        first: Number.parseInt(entry.slice(0, firstDot), 36),
        last: Number.parseInt(entry.slice(firstDot + 1, secondDot), 36),
        text: entry.slice(secondDot + 1),
      };
    })
    .sort((a, b) => a.first - b.first);
}

/** The text of the range containing a code point, or null. */
function rangeLookup(ranges: readonly Range[]) {
  const firsts = ranges.map((range) => range.first);
  return (codePoint: number): string | null => {
    const range = ranges[floorIndex(firsts, codePoint)];
    return range && codePoint <= range.last ? range.text : null;
  };
}

const ALIAS_KEYS: Readonly<Record<string, keyof NameAliases>> = {
  c: "control",
  a: "abbreviation",
  r: "correction",
  l: "alternate",
};

function parseAliases(encoded: string): Map<number, NameAliases> {
  const aliases = new Map<number, NameAliases>();
  for (const entry of encoded ? encoded.split("|") : []) {
    const [codePoint, type, alias] = entry.split(":");
    const key = ALIAS_KEYS[type];
    if (!key) {
      continue;
    }
    const value = Number.parseInt(codePoint, 36);
    aliases.set(value, { ...aliases.get(value), [key]: alias });
  }
  return aliases;
}

function parseEntities(encoded: string): Map<number, string[]> {
  const entities = new Map<number, string[]>();
  for (const entry of encoded ? encoded.split("|") : []) {
    const colon = entry.indexOf(":");
    entities.set(
      Number.parseInt(entry.slice(0, colon), 36),
      entry.slice(colon + 1).split(",")
    );
  }
  return entities;
}

function parseNames(lines: readonly string[]): Map<number, string> {
  const names = new Map<number, string>();
  let codePoint = -1;
  let words: string[] = [];
  for (const line of lines) {
    if (line.length === 0) {
      continue;
    }
    const colon = line.indexOf(":");
    codePoint += colon === -1 ? 1 : Number.parseInt(line.slice(0, colon), 36);
    const shared = SHARED_DIGITS.indexOf(line.charAt(colon + 1));
    const added = line.slice(colon + 2);
    words = words.slice(0, shared);
    if (added) {
      words.push(...added.split(" "));
    }
    names.set(codePoint, words.join(" "));
  }
  return names;
}

function splitTables(text: string): {
  tables: Map<string, string>;
  nameLines: string[];
} {
  const lines = text.split(LINE_BREAK);
  const tables = new Map<string, string>();
  let index = 0;
  for (; index < lines.length; index++) {
    const line = lines[index];
    if (line === "@names") {
      index++;
      break;
    }
    if (line.startsWith("@")) {
      const space = line.indexOf(" ");
      tables.set(
        line.slice(1, space === -1 ? undefined : space),
        space === -1 ? "" : line.slice(space + 1)
      );
    }
  }
  return { tables, nameLines: lines.slice(index) };
}

function hexOf(codePoint: number): string {
  return codePoint.toString(16).toUpperCase().padStart(4, "0");
}

/** Decode the text of `unicode-names.txt`. */
export function parseUnicodeData(text: string): UnicodeDatabase {
  const { tables, nameLines } = splitTables(text);
  const table = (key: string) => tables.get(key) ?? "";
  const names = parseNames(nameLines);
  const rule = rangeLookup(parseRanges(table("rules")));
  const block = rangeLookup(parseRanges(table("blocks")));
  const aliases = parseAliases(table("aliases"));
  const entities = parseEntities(table("entities"));
  return {
    version: table("version"),
    name: (codePoint) => {
      const stored = names.get(codePoint);
      if (stored) {
        return stored;
      }
      const hangul = hangulSyllableName(codePoint);
      if (hangul) {
        return hangul;
      }
      const prefix = rule(codePoint);
      return prefix === null ? null : `${prefix}${hexOf(codePoint)}`;
    },
    aliases: (codePoint) => aliases.get(codePoint) ?? {},
    generalCategory: runLookup(
      parseRuns(table("gcRuns")),
      table("gc").split(" "),
      "Cn"
    ),
    script: runLookup(
      parseRuns(table("scriptRuns")),
      table("scripts").split(" "),
      "Unknown"
    ),
    block: (codePoint) => block(codePoint) ?? "No_Block",
    age: runLookup(
      parseRuns(table("ageRuns")),
      table("ages").split(" "),
      "unassigned"
    ),
    entities: (codePoint) => entities.get(codePoint) ?? [],
  };
}

let database: Promise<UnicodeDatabase> | null = null;

/**
 * The database, fetched once per page load and kept in memory. A failed fetch is
 * forgotten, so the next call tries again.
 */
export function loadUnicodeData(): Promise<UnicodeDatabase> {
  if (!database) {
    database = fetch(UNICODE_DATA_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unicode data returned ${response.status}`);
        }
        return response.text();
      })
      .then(parseUnicodeData)
      .catch((error: unknown) => {
        database = null;
        throw error;
      });
  }
  return database;
}

/* -------------------------------------------------------- labels and names */

const NONCHARACTER_FIRST = 0xfd_d0;
const NONCHARACTER_LAST = 0xfd_ef;

/** U+FDD0–U+FDEF and the last two code points of every plane. */
export function isNoncharacter(codePoint: number): boolean {
  return (
    (codePoint >= NONCHARACTER_FIRST && codePoint <= NONCHARACTER_LAST) ||
    codePoint % 0x1_00_00 >= 0xff_fe
  );
}

/**
 * The code point label Unicode uses where a character has no name (Unicode Standard
 * section 4.8): `<control-0009>`, `<private-use-E000>`, `<surrogate-D800>`,
 * `<noncharacter-FFFF>` or `<reserved-0378>`.
 */
export function codePointLabel(codePoint: number, category: string): string {
  const hex = hexOf(codePoint);
  if (category === "Cc") {
    return `<control-${hex}>`;
  }
  if (category === "Co") {
    return `<private-use-${hex}>`;
  }
  if (category === "Cs") {
    return `<surrogate-${hex}>`;
  }
  if (isNoncharacter(codePoint)) {
    return `<noncharacter-${hex}>`;
  }
  return `<reserved-${hex}>`;
}

/** General_Category long names (PropertyValueAliases.txt), by short name. */
export const GENERAL_CATEGORIES: Readonly<Record<string, string>> = {
  Lu: "Uppercase Letter",
  Ll: "Lowercase Letter",
  Lt: "Titlecase Letter",
  Lm: "Modifier Letter",
  Lo: "Other Letter",
  Mn: "Nonspacing Mark",
  Mc: "Spacing Mark",
  Me: "Enclosing Mark",
  Nd: "Decimal Number",
  Nl: "Letter Number",
  No: "Other Number",
  Pc: "Connector Punctuation",
  Pd: "Dash Punctuation",
  Ps: "Open Punctuation",
  Pe: "Close Punctuation",
  Pi: "Initial Punctuation",
  Pf: "Final Punctuation",
  Po: "Other Punctuation",
  Sm: "Math Symbol",
  Sc: "Currency Symbol",
  Sk: "Modifier Symbol",
  So: "Other Symbol",
  Zs: "Space Separator",
  Zl: "Line Separator",
  Zp: "Paragraph Separator",
  Cc: "Control",
  Cf: "Format",
  Cs: "Surrogate",
  Co: "Private Use",
  Cn: "Unassigned",
};
